import { useState } from "react";
import { FastProject } from "@typedefs/interfaces";
import { useAddTagToProjectMutation, useDeleteTagFromProjectMutation } from "@hooks/index";
import { Option } from "@typedefs/modulesInterfaces";
import useErrorMessage from "@utils/useErrorMessage";

interface UseEditTagsReturn {
    isEditTagsModalOpen: boolean;
    showEditTagsModal: (project: FastProject) => void;
    closeEditTagsModal: () => void;
    handleSubmitTags: (initialTags: Option[], newTags: Option[]) => Promise<void>;
    projectToEditTags: FastProject | null;
    isLoading: boolean;
    error: string;
    isEditTagsDialogErrorMessageOpen: boolean;
    closeEditTagsDialogErrorMessage: () => void;
}

const useEditTags = (refetch?: () => void): UseEditTagsReturn => {
    const [isEditTagsModalOpen, setEditTagsModalOpen] = useState(false);
    const [projectToEditTags, setProjectToEditTags] = useState<FastProject | null>(null);
    const [error, setError] = useState<string>("");
    const [isEditTagsDialogErrorMessageOpen, setIsEditTagsDialogErrorMessageOpen] = useState(false);

    const [addTagToProject, { isLoading: isAddingTag, isError: isAddError, error: addError }] = useAddTagToProjectMutation();
    const [deleteTagFromProject, { isLoading: isDeletingTag, isError: isDeleteError, error: deleteError }] = useDeleteTagFromProjectMutation();

    const { errorMsg: addErrorMsg } = useErrorMessage({ isError: isAddError, error: addError });
    const { errorMsg: deleteErrorMsg } = useErrorMessage({ isError: isDeleteError, error: deleteError });

    const showEditTagsModal = (project: FastProject) => {
        setEditTagsModalOpen(true);
        setProjectToEditTags(project);
        setError("");
    }

    const closeEditTagsModal = () => {
        setEditTagsModalOpen(false);
        setError("");
    }

    const closeEditTagsDialogErrorMessage = () => {
        setIsEditTagsDialogErrorMessageOpen(false);
        setError("");
    }

    const handleSubmitTags = async (initialTags: Option[], newTags: Option[]) => {
        try {
            const tagsToAdd = newTags.filter(tag => !initialTags.some(t => t.id === tag.id));
            const tagsToDelete = initialTags.filter(tag => !newTags.some(t => t.id === tag.id));

            if (tagsToAdd.length === 0 && tagsToDelete.length === 0) {
                closeEditTagsModal();
                return;
            }

            const addTagsPromises = tagsToAdd.map(tag =>
                addTagToProject({
                    projectId: projectToEditTags?.id ?? 0,
                    name: tag.name
                }).unwrap()
            );

            const deleteTagsPromises = tagsToDelete.map(tag =>
                deleteTagFromProject({
                    projectId: projectToEditTags?.id ?? 0,
                    tagId: tag.id
                }).unwrap()
            );

            await Promise.all([...addTagsPromises, ...deleteTagsPromises]);

            refetch?.();
            closeEditTagsModal();
        } catch (error) {
            console.error("Error adding or deleting tags: ", error);
            setError(addErrorMsg || deleteErrorMsg || "Failed to update tags");
        }
    }

    return {
        isEditTagsModalOpen,
        showEditTagsModal,
        closeEditTagsModal,
        projectToEditTags,
        handleSubmitTags,
        isLoading: isAddingTag || isDeletingTag,
        error,
        isEditTagsDialogErrorMessageOpen,
        closeEditTagsDialogErrorMessage,
    }
}

export default useEditTags;